import { useTranslation } from "react-i18next";

import {
  CASE_CONFIG,
  DECISION_COMMENT_METADATA_KEY,
  DECISION_METADATA_KEY,
} from "@/domain/case/case-config";

export type ReturnedReasonProps = {
  /**
   * 案件の metadata。一覧は未設定を null で持ち、詳細は key を落とすので
   * どちらも受けられる形にする。
   */
  readonly metadata: Readonly<Record<string, string | null | undefined>>;
};

/**
 * 差し戻しの理由。
 *
 * 差し戻された人が最初に知りたいのは「なぜ戻ったか」なので、直す場所
 * （再入力画面）と案件の概要の両方に出す。判定は metadata から読むので、
 * モデルごとに表示側を書き分けなくて済む。
 *
 * 差し戻し以外では**何も出さない**。前回の指摘が現在の指摘に見えてしまう。
 */
export function ReturnedReason({ metadata }: ReturnedReasonProps) {
  const { t } = useTranslation("common");

  if (metadata[DECISION_METADATA_KEY] !== CASE_CONFIG.rejectionValue) {
    return null;
  }

  const comment = metadata[DECISION_COMMENT_METADATA_KEY] ?? "";

  return (
    <section className="border-destructive/40 bg-destructive/5 flex flex-col gap-1 rounded-md border px-3 py-2">
      <h2 className="text-sm font-semibold">
        {t("caseDetail.returnedReason")}
      </h2>
      <p className="text-sm whitespace-pre-wrap">
        {comment === "" ? t("caseDetail.returnedReasonEmpty") : comment}
      </p>
    </section>
  );
}

/**
 * 一覧に添える 1 行版。枠は付けず、理由の本文だけを控えめに出す
 * （行が主役なので、案件ごとに赤い枠が並ぶと一覧が読みにくい）。
 */
export function ReturnedReasonLine({ metadata }: ReturnedReasonProps) {
  const { t } = useTranslation("common");

  // 枠つき版と同じ判断（呼び出し側のガードに頼らない）
  if (metadata[DECISION_METADATA_KEY] !== CASE_CONFIG.rejectionValue) {
    return null;
  }

  const comment = metadata[DECISION_COMMENT_METADATA_KEY] ?? "";

  return (
    <p className="text-muted-foreground flex flex-wrap gap-1 text-xs">
      <span>{t("caseDetail.returnedReason")}:</span>
      <span>
        {comment === "" ? t("caseDetail.returnedReasonEmpty") : comment}
      </span>
    </p>
  );
}
